/**
 * PropertyCapConfirmCard — Slice 2 human confirm/reject of the property CAP (limit + deductible).
 *
 * MONEY-FENCE (the whole point): the parser/LLM only ever PROPOSE (data.policy_proposals). The LIVE
 * cap (data.policy.property_limit / property_deductible — what the coverage engine + the election wrap
 * read) becomes real ONLY when an authorized human CONFIRMS a proposed candidate (or types an explicit
 * override) via the attested PATCH. Until then live is empty → payable is UNCAPPED → we say so.
 *
 * This component NEVER invents, sums, or hand-formats a dollar. Every figure renders through
 * <FinancialValue>. The only numeric conversion is parsing the human's typed override string for the
 * PATCH body. After any write it re-reads server truth (never sets `live` locally). Seat-gated to the
 * write roles (owner/admin/data_specialist); everyone else sees the same rows read-only. Design per the
 * K3 gate; mounts as a sibling band below PropertyElectionBand in ReconciliationWorkbench.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { FinancialValue } from '../shared/FinancialValue';
import useWorkspaceRole from '../../hooks/useWorkspaceRole';
import {
  getPolicyCap,
  confirmPolicyCap,
  rejectPolicyCap,
  type PolicyCapState,
  type PolicyCapField,
  type PolicyCapCandidate,
  type PropertyReconciliation,
} from '../../services/propertyReconciliationApi';

// ── Shared chrome (K3 gate §0) ────────────────────────────────────────────────────────────────
const SEG_BASE =
  'px-3 h-8 text-[11px] uppercase tracking-[0.06em] font-mono border disabled:opacity-40 disabled:cursor-not-allowed';
const SEG_ON = SEG_BASE + ' border-[var(--color-brand)] text-[var(--color-brand)] bg-transparent';
const SEG_OFF =
  SEG_BASE + ' border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]';
const SEG_RED = SEG_BASE + ' border-[var(--color-blocker)] text-[var(--color-blocker)]';

const CHIP = 'text-[9px] uppercase tracking-[0.08em] px-1.5 py-0.5 border font-mono';
// Band (aggregate) chip = FILLED — the "this card needs you" beacon. Row chip = OUTLINE-only, so
// three needs-confirm chips don't stack into orange mass (K3 pixel-review nit 2).
const CHIP_ORANGE =
  CHIP + ' border-[var(--color-needs-human-tint-bd)] bg-[var(--color-needs-human-tint)] text-[var(--color-brand)]'; // band beacon
const CHIP_ORANGE_ROW = CHIP + ' border-[var(--color-brand)] text-[var(--color-brand)]'; // row, transparent outline
const CHIP_GRAY = CHIP + ' border-[var(--color-border)] text-[var(--color-verified)]'; // settled
const CHIP_MUTED = CHIP + ' border-[var(--color-border)] text-[var(--color-text-muted)]';
const CHIP_SEC = CHIP + ' border-[var(--color-border)] text-[var(--color-text-secondary)]';
// The parser's proposed pick renders in orange with a left bar — a three-state money code: orange =
// PROPOSED, white = LIVE (confirmed), absent = UNCAPPED (K3 nit 3).
const PICK_PRIMARY = 'pl-2 border-l-2 border-[var(--color-brand)] text-[var(--color-brand)]';

const FIELD_LABEL = 'w-24 shrink-0 text-[9px] uppercase tracking-[0.08em] font-mono text-[var(--color-text-muted)]';
const META = 'text-[9px] uppercase tracking-[0.08em] font-mono text-[var(--color-text-muted)]';
const PROV = 'text-[10px] font-mono text-[var(--color-text-muted)]';
const QUIET =
  'h-6 px-1 text-[10px] uppercase tracking-[0.06em] font-mono text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]';
const INDENT = 'ml-[104px]'; // w-24 label + gap-2 → aligns secondary lines under content
const ERR_LINE = 'text-[10px] font-mono text-[var(--color-blocker)]';
const OVERRIDE_INPUT =
  'h-8 w-28 px-2 text-[11px] font-mono text-right bg-transparent border border-[var(--color-border)] ' +
  'text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand)] focus:outline-none';

// ── Helpers (no money math — display + the single permitted override parse) ──────────────────────
function confPct(c: number | undefined): number | null {
  if (c == null || !Number.isFinite(c)) return null;
  return c <= 1 ? Math.round(c * 100) : Math.round(c);
}
function fmtDate(iso: unknown): string {
  if (typeof iso !== 'string' || !iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function asStr(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

// Defensive: the real backend always returns the full shape, but a degraded/empty response (or the
// offline harness default {}) must NEVER white-screen the workbench via `state.live[field]`. Coerce
// any 200 response to the guaranteed shape; a genuine 404 stays null (→ render nothing).
function normalizeState(s: PolicyCapState | null, reportId: string): PolicyCapState | null {
  if (s == null) return null;
  const anyS = s as any;
  // A real GET always carries a `live` block (with null caps on a gold/BI case — an honest "none on
  // file"). A bare {} (degraded / unmatched response) has none of these keys: render NOTHING rather
  // than assert "no property limit on file" from absent data (Fable nit).
  if (anyS.live == null && anyS.proposals == null && anyS.confirmations == null) return null;
  const live = anyS.live && typeof anyS.live === 'object' ? anyS.live : {};
  return {
    report_id: typeof anyS.report_id === 'string' ? anyS.report_id : reportId,
    proposals: anyS.proposals && typeof anyS.proposals === 'object' ? anyS.proposals : {},
    live: {
      property_limit: live.property_limit ?? null,
      property_deductible: live.property_deductible ?? null,
    },
    confirmations: anyS.confirmations && typeof anyS.confirmations === 'object' ? anyS.confirmations : {},
  };
}

type ErrShape = { status?: number; message?: string } | null;
function readErr(e: any): { status?: number; message?: string } {
  const status = e?.status ?? e?.response?.status;
  const message = e?.body?.detail ?? e?.response?.data?.detail ?? e?.message;
  return { status, message: typeof message === 'string' ? message : undefined };
}

type Presentation = 'confirmed' | 'proposed' | 'rejected' | 'empty';

// Per-field UI state (no live dollar ever held here — overrideRaw is the human's typed string only).
interface FieldUI {
  sel: number;
  overrideRaw: string;
  rejectArmed: boolean;
  ackBi: boolean;
  editing: boolean; // "change" on a confirmed field
  manual: boolean; // "enter manually" on an empty field
  busy: boolean;
  error: ErrShape;
}
const FIELD_UI0: FieldUI = {
  sel: 0,
  overrideRaw: '',
  rejectArmed: false,
  ackBi: false,
  editing: false,
  manual: false,
  busy: false,
  error: null,
};

const FIELDS: { field: PolicyCapField; label: string }[] = [
  { field: 'property_limit', label: 'limit of insurance' },
  { field: 'property_deductible', label: 'deductible' },
];

interface Props {
  reconciliation: PropertyReconciliation | null;
}

const PropertyCapConfirmCard: React.FC<Props> = ({ reconciliation }) => {
  const reportId = reconciliation?.report_id ?? '';
  // Write seats = backend _ELECTION_WRITE_ROLES {owner, admin, data_specialist}. useWorkspaceRole
  // normalizes owner→admin (no 'owner' literal client-side), so admin + data_specialist cover it.
  // Client gating is UX only — the PATCH is the real enforcement (403s a wrong seat).
  const role = useWorkspaceRole();
  const canAct = role === 'admin' || role === 'data_specialist';

  const [state, setState] = useState<PolicyCapState | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [ui, setUi] = useState<Record<PolicyCapField, FieldUI>>({
    property_limit: { ...FIELD_UI0 },
    property_deductible: { ...FIELD_UI0 },
  });

  useEffect(() => {
    let live = true;
    if (!reportId) return;
    getPolicyCap(reportId)
      .then((s) => {
        if (live) {
          setState(normalizeState(s, reportId));
          setLoaded(true);
        }
      })
      .catch(() => {
        if (live) setLoaded(true);
      });
    return () => {
      live = false;
    };
  }, [reportId]);

  const patchUi = (field: PolicyCapField, patch: Partial<FieldUI>) =>
    setUi((s) => ({ ...s, [field]: { ...s[field], ...patch } }));
  const resetUi = (field: PolicyCapField) => setUi((s) => ({ ...s, [field]: { ...FIELD_UI0 } }));

  const refetch = async () => {
    const s = await getPolicyCap(reportId);
    setState(normalizeState(s, reportId));
  };

  // Server-truth presentation for a field (before UI overrides like editing/manual are applied).
  const baseState = (field: PolicyCapField): Presentation => {
    if (!state) return 'empty';
    if (state.live[field] != null) return 'confirmed';
    const p = state.proposals?.[field];
    if (p?.status === 'proposed' && (p.candidates?.length ?? 0) > 0) return 'proposed';
    if (p?.status === 'rejected') return 'rejected';
    return 'empty';
  };

  const anyProposed = useMemo(
    () => FIELDS.some(({ field }) => baseState(field) === 'proposed'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state],
  );
  const allSettled = useMemo(
    () => FIELDS.every(({ field }) => baseState(field) === 'confirmed'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state],
  );

  // ── write actions ──────────────────────────────────────────────────────────────────────────
  // The write and the re-read are SEPARATE try blocks: a write succeeds → we re-read server truth,
  // but a failed re-read must NEVER render as "no change was written" (the cap IS live). A forensic
  // tool must not make a false statement about live state.
  const doConfirm = async (
    field: PolicyCapField,
    opts: { fromCandidateIndex?: number; value?: number; expectedValue?: number },
  ) => {
    patchUi(field, { busy: true, error: null });
    try {
      await confirmPolicyCap(reportId, field, {
        ...opts,
        ...(ui[field].ackBi ? { biDollarDeductibleAcknowledged: true } : {}),
      });
    } catch (e: any) {
      patchUi(field, { busy: false, error: readErr(e) });
      return;
    }
    resetUi(field);
    try {
      await refetch();
    } catch {
      patchUi(field, { error: { message: 'Written — could not re-read server state; refresh to verify.' } });
    }
  };

  const confirmField = (field: PolicyCapField, candidates: PolicyCapCandidate[]) => {
    const raw = ui[field].overrideRaw.trim();
    if (raw !== '') {
      const v = Number(raw);
      if (!Number.isFinite(v)) {
        patchUi(field, { error: { message: 'Enter a valid dollar amount.' } });
        return;
      }
      void doConfirm(field, { value: v }); // typed override wins
    } else if (candidates.length > 0) {
      const sel = Math.min(Math.max(0, ui[field].sel), candidates.length - 1); // clamp to a real index
      // expectedValue = the exact dollar this card is DISPLAYING for that candidate; the backend 409s
      // if a re-parse changed it, so the human can never attest a value they didn't see (TOCTOU fence).
      void doConfirm(field, { fromCandidateIndex: sel, expectedValue: candidates[sel]?.value });
    } else {
      patchUi(field, { error: { message: 'Enter a dollar amount to confirm.' } });
    }
  };

  const rejectField = async (field: PolicyCapField) => {
    patchUi(field, { busy: true, error: null });
    try {
      await rejectPolicyCap(reportId, field);
    } catch (e: any) {
      patchUi(field, { busy: false, error: readErr(e) });
      return;
    }
    resetUi(field);
    try {
      await refetch();
    } catch {
      patchUi(field, { error: { message: 'Rejected — could not re-read server state; refresh to verify.' } });
    }
  };

  // ── render guards ────────────────────────────────────────────────────────────────────────────
  if (!reportId) return null;
  if (loaded && state == null) return null; // 404 → genuinely no property dimension → render nothing
  if (!state) return null; // still loading → render nothing (no skeleton, per the language)

  return (
    <div className="px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      {/* header row — identical rhythm to PropertyElectionBand */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono">
            property cap confirmation
          </span>
          {anyProposed && <span className={CHIP_ORANGE}>needs confirm</span>}
          {!anyProposed && allSettled && <span className={CHIP_GRAY}>settled</span>}
          {!anyProposed && !allSettled && <span className={CHIP_MUTED}>uncapped</span>}
        </div>
        <span className="text-[10px] text-[var(--color-text-muted)]">
          The parser proposes; a human confirms. Until confirmed, nothing is live — no live limit means payable is
          uncapped.
        </span>
      </div>

      <div className="mt-3 space-y-3">
        {FIELDS.map(({ field, label }, i) => (
          <div key={field} className={i > 0 ? 'pt-3 border-t border-[var(--color-border)]' : undefined}>
            {renderField(field, label)}
          </div>
        ))}
      </div>
    </div>
  );

  // ── per-field renderer ─────────────────────────────────────────────────────────────────────
  function renderField(field: PolicyCapField, label: string) {
    const u = ui[field];
    const base = baseState(field);

    // UI-driven presentations take precedence over the settled/empty/rejected base.
    if (u.editing && base === 'confirmed') return renderEditing(field, label);
    // "re-open" a rejected field and "enter manually" an empty one both land in the manual typed-value flow.
    if (u.manual && (base === 'empty' || base === 'rejected')) return renderManual(field, label);

    switch (base) {
      case 'confirmed':
        return renderConfirmed(field, label);
      case 'proposed':
        return renderProposed(field, label);
      case 'rejected':
        return renderRejected(field, label);
      default:
        return renderEmpty(field, label);
    }
  }

  // STATE 1 — CONFIRMED (one line, neutral gray)
  function renderConfirmed(field: PolicyCapField, label: string) {
    const conf = (state!.confirmations || {})[field] || {};
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className={FIELD_LABEL}>{label}</span>
        <FinancialValue value={state!.live[field]} size="sm" cents={false} />
        <span className={CHIP_GRAY}>confirmed</span>
        {conf.override ? <span className={CHIP_SEC}>override</span> : null}
        <span className={PROV} title={asStr(conf.confirmed_by)}>
          confirmed by {asStr(conf.confirmed_role) || 'staff'} · {fmtDate(conf.confirmed_at)}
        </span>
        {canAct && (
          <button type="button" className={'ml-auto ' + QUIET} onClick={() => patchUi(field, { editing: true, error: null })}>
            change
          </button>
        )}
      </div>
    );
  }

  // STATE 2 — PROPOSED (the action state)
  function renderProposed(field: PolicyCapField, label: string) {
    const u = ui[field];
    const proposal = state!.proposals?.[field];
    const candidates: PolicyCapCandidate[] = proposal?.candidates ?? [];
    const sel = Math.min(u.sel, Math.max(0, candidates.length - 1));
    const selected = candidates[sel];
    const overriding = u.overrideRaw.trim() !== '';
    const gateDOpen = field === 'property_deductible' && u.error?.status === 409;
    const pct = confPct(selected?.confidence);

    return (
      <div>
        {/* Row 1 — pick + actions. CONF/source/proposed move to the evidence line so the action
            cluster stays inline on this row instead of wrapping below it (K3 nit 1). */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={FIELD_LABEL}>{label}</span>
          <span className={CHIP_ORANGE_ROW}>needs confirm</span>

          {candidates.length === 1 && (
            <span className={overriding ? 'opacity-50 ' + PICK_PRIMARY : PICK_PRIMARY}>
              <FinancialValue value={candidates[0].value} compact cents={false} />
            </span>
          )}

          {candidates.length > 1 && (
            <div
              className={
                'flex items-stretch border border-[var(--color-border)] divide-x divide-[var(--color-border)] ' +
                (overriding ? 'opacity-50' : '')
              }
            >
              {candidates.slice(0, 3).map((c, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => patchUi(field, { sel: idx, ackBi: false, error: null })}
                  className={
                    'px-2 h-7 text-[11px] font-mono ' +
                    (idx === sel && !overriding
                      ? 'border-l-2 border-[var(--color-brand)] text-[var(--color-brand)]'
                      : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]')
                  }
                >
                  <FinancialValue value={c.value} compact cents={false} />
                </button>
              ))}
            </div>
          )}

          {canAct ? (
            <div className="ml-auto flex items-center gap-2">
              {!u.rejectArmed ? (
                <>
                  <button
                    type="button"
                    className={SEG_ON}
                    disabled={u.busy || (gateDOpen && !u.ackBi)}
                    onClick={() => confirmField(field, candidates)}
                  >
                    {u.busy ? 'confirming…' : overriding ? 'confirm override' : 'confirm'}
                  </button>
                  <button
                    type="button"
                    className={SEG_OFF}
                    disabled={u.busy}
                    onClick={() => patchUi(field, { rejectArmed: true, error: null })}
                  >
                    reject
                  </button>
                  {!overriding && <span className={META}>or</span>}
                  <input
                    value={u.overrideRaw}
                    inputMode="numeric"
                    placeholder="override $"
                    // reset ackBi/error on edit so a Gate-D acknowledgment stays tied to the exact dollar
                    onChange={(e) => patchUi(field, { overrideRaw: e.target.value.replace(/[^0-9.]/g, ''), ackBi: false, error: null })}
                    className={OVERRIDE_INPUT}
                  />
                </>
              ) : (
                <>
                  <span className="text-[10px] uppercase tracking-[0.06em] font-mono text-[var(--color-text-primary)]">
                    decline this proposal?
                  </span>
                  <button type="button" className={SEG_RED} disabled={u.busy} onClick={() => void rejectField(field)}>
                    {u.busy ? 'rejecting…' : 'confirm reject'}
                  </button>
                  <button type="button" className={QUIET} onClick={() => patchUi(field, { rejectArmed: false })}>
                    cancel
                  </button>
                </>
              )}
            </div>
          ) : (
            <span className={'ml-auto ' + META}>read-only · awaiting authorized confirm</span>
          )}
        </div>

        {/* Row 2 — evidence: verbatim citation · source · confidence · proposed date, one dense line */}
        <div className={INDENT + ' mt-2 border-l-2 border-[var(--color-border)] pl-3 py-0.5'}>
          <span className="text-[10px] font-mono text-[var(--color-text-secondary)] leading-snug">
            <span className={META}>policy text</span> {selected?.cite ?? 'no citation captured'}
            {selected?.source ? <span className="text-[var(--color-text-muted)]"> · {selected.source}</span> : null}
            {pct != null ? <span className="text-[var(--color-text-muted)]"> · conf {pct}%</span> : null}
            {selected?.proposed_at ? (
              <span className="text-[var(--color-text-muted)]"> · proposed {fmtDate(selected.proposed_at)}</span>
            ) : null}
          </span>
        </div>

        {/* Gate D / errors */}
        {renderFieldError(field)}
      </div>
    );
  }

  // STATE 1→edit — "change" a confirmed cap. TYPED override ONLY (never promote a candidate the human
  // can't see here): replacing a LIVE cap must be a value the human explicitly typed. Confirm is disabled
  // until they type one. A live cap can only be replaced by another confirmed value, never rejected to null.
  function renderEditing(field: PolicyCapField, label: string) {
    const u = ui[field];
    const overriding = u.overrideRaw.trim() !== '';
    const gateDOpen = field === 'property_deductible' && u.error?.status === 409;

    return (
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={FIELD_LABEL}>{label}</span>
          <span className="opacity-50">
            <FinancialValue value={state!.live[field]} size="sm" cents={false} />
          </span>
          <span className={CHIP_MUTED}>editing — live value unchanged until confirm</span>

          <div className="ml-auto flex items-center gap-2">
            <input
              value={u.overrideRaw}
              inputMode="numeric"
              placeholder="new $"
              onChange={(e) => patchUi(field, { overrideRaw: e.target.value.replace(/[^0-9.]/g, ''), ackBi: false, error: null })}
              className={OVERRIDE_INPUT}
            />
            <button
              type="button"
              className={SEG_ON}
              disabled={!overriding || u.busy || (gateDOpen && !u.ackBi)}
              onClick={() => confirmField(field, [])}
            >
              {u.busy ? 'confirming…' : 'confirm'}
            </button>
            <button type="button" className={QUIET} onClick={() => resetUi(field)}>
              cancel
            </button>
          </div>
        </div>
        {renderFieldError(field)}
      </div>
    );
  }

  // STATE 3 — REJECTED (one line)
  function renderRejected(field: PolicyCapField, label: string) {
    const rejection = (state!.proposals?.[field]?.rejection || {}) as Record<string, unknown>;
    const tail =
      field === 'property_limit'
        ? '— no property limit on file; payable uncapped'
        : '— no property deductible on file';
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className={FIELD_LABEL}>{label}</span>
        <span className={CHIP_SEC}>rejected</span>
        <span className={PROV}>
          reviewed and declined by {asStr(rejection.rejected_role) || 'staff'} · {fmtDate(rejection.rejected_at)} {tail}
        </span>
        {canAct ? (
          <button type="button" className={'ml-auto ' + QUIET} onClick={() => patchUi(field, { manual: true, error: null })}>
            re-open
          </button>
        ) : (
          <span className={'ml-auto ' + META}>uncapped</span>
        )}
      </div>
    );
  }

  // STATE 4 — NOTHING ON FILE (one honest line; authorized humans may enter manually)
  function renderEmpty(field: PolicyCapField, label: string) {
    const copy =
      field === 'property_limit'
        ? 'No property limit on file. Payable is uncapped.'
        : 'No property deductible on file.';
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className={FIELD_LABEL}>{label}</span>
        <span className={CHIP_MUTED}>none on file</span>
        <span className={PROV}>{copy}</span>
        {canAct && (
          <button type="button" className={'ml-auto ' + QUIET} onClick={() => patchUi(field, { manual: true, error: null })}>
            enter manually
          </button>
        )}
      </div>
    );
  }

  // STATE 4→manual — low-key typed-override confirm (also the "re-open" target for a rejected field)
  function renderManual(field: PolicyCapField, label: string) {
    const u = ui[field];
    const gateDOpen = field === 'property_deductible' && u.error?.status === 409;
    return (
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={FIELD_LABEL}>{label}</span>
          <span className={CHIP_MUTED}>enter manually</span>
          <div className="ml-auto flex items-center gap-2">
            <input
              value={u.overrideRaw}
              inputMode="numeric"
              placeholder="$"
              onChange={(e) => patchUi(field, { overrideRaw: e.target.value.replace(/[^0-9.]/g, ''), ackBi: false, error: null })}
              className={OVERRIDE_INPUT}
            />
            <button
              type="button"
              className={SEG_ON}
              disabled={!u.overrideRaw.trim() || u.busy || (gateDOpen && !u.ackBi)}
              onClick={() => confirmField(field, [])}
            >
              {u.busy ? 'confirming…' : 'confirm'}
            </button>
            <button type="button" className={QUIET} onClick={() => resetUi(field)}>
              cancel
            </button>
          </div>
        </div>
        {renderFieldError(field)}
      </div>
    );
  }

  // LINE C — error / Gate-D reveal (per field, cleared on retry/success)
  function renderFieldError(field: PolicyCapField) {
    const u = ui[field];
    if (!u.error) return null;
    const { status, message } = u.error;

    if (field === 'property_deductible' && status === 409) {
      return (
        <div className={INDENT + ' mt-1.5 space-y-1.5'}>
          <p className={ERR_LINE}>{message || 'A BI-ledger dollar deductible is already live.'}</p>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={u.ackBi}
              onChange={(e) => patchUi(field, { ackBi: e.target.checked })}
              className="mt-px h-3 w-3 accent-[var(--color-brand)]"
            />
            <span className="text-[10px] font-mono text-[var(--color-text-secondary)] leading-snug">
              A business-income deductible is already on file — this property deductible is separate and applies only to
              the property loss.
            </span>
          </label>
        </div>
      );
    }
    if (status === 403) {
      return (
        <div className={INDENT + ' mt-1.5'}>
          <p className={ERR_LINE}>{message || 'Your seat is not authorized to write a property cap (403).'}</p>
        </div>
      );
    }
    return (
      <div className={INDENT + ' mt-1.5'}>
        <p className={ERR_LINE}>Confirm failed — no change was written. {message || 'Retry.'}</p>
      </div>
    );
  }
};

export default PropertyCapConfirmCard;
